import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Check, MessageCircle, ArrowLeft } from 'lucide-react';
import { productsData } from '@/data/productsData';

const CompareProductsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [compareProducts, setCompareProducts] = useState([]);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) {
      const productIds = ids.split(',').map(Number);
      const products = productsData.filter(p => productIds.includes(p.id));
      setCompareProducts(products);
    }
  }, [searchParams]);

  const removeProduct = (id) => {
    const updated = compareProducts.filter(p => p.id !== id);
    setCompareProducts(updated);
    if (updated.length === 0) {
      navigate('/products');
    }
  };

  const getWhatsAppLink = (product) => {
    const message = `Hi NutraFuel, I want to order ${product.name} – ${product.brand}. Please share availability and best price.`;
    return `https://wa.me/9779824042067?text=${encodeURIComponent(message)}`;
  };

  if (compareProducts.length === 0) {
    return (
      <Layout>
        <section className="py-32 text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold mb-4">No Products Selected</h1>
            <p className="text-muted-foreground mb-6">Select products from the products page to compare</p>
            <Button onClick={() => navigate('/products')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go to Products
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-12 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/products')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Button>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
            Compare <span className="text-primary">Supplements</span>
          </h1>
          <p className="text-lg text-muted-foreground">
            Side-by-side comparison to help you make the right choice
          </p>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          {/* Mobile Warning */}
          <div className="lg:hidden bg-gold/10 border border-gold/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-foreground">
              💡 <strong>Tip:</strong> Rotate your device for a better comparison view
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Product Cards Row */}
              <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `repeat(${compareProducts.length}, 1fr)` }}>
                {compareProducts.map((product) => (
                  <div key={product.id} className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
                    {/* Product Image */}
                    <div className="relative aspect-square bg-muted">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeProduct(product.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-destructive/90 hover:bg-destructive text-destructive-foreground rounded-full flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {product.popular && (
                        <Badge className="absolute top-2 left-2 bg-gold text-foreground">
                          Popular
                        </Badge>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <span className="text-xs text-gold font-medium uppercase tracking-wider block mb-1">
                        {product.brand}
                      </span>
                      <h3 className="font-bold text-foreground text-lg mb-3">
                        {product.name}
                      </h3>
                      <Badge variant={product.stock === 'In Stock' ? 'success' : 'warning'} className="mb-4">
                        {product.stock}
                      </Badge>

                      {/* Order Button */}
                      <Button
                        variant="default"
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        asChild
                      >
                        <a href={getWhatsAppLink(product)} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Order on WhatsApp
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Comparison Rows */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Nutritional Information */}
                <div className="border-b border-border bg-muted/50">
                  <div className="p-3">
                    <h3 className="font-bold text-foreground text-sm uppercase tracking-wide">
                      Nutritional Information
                    </h3>
                  </div>
                </div>

                <ComparisonRow label="Protein per Serving" values={compareProducts.map(p => p.protein)} />
                <ComparisonRow label="Calories per Serving" values={compareProducts.map(p => p.calories)} />
                <ComparisonRow label="Servings per Container" values={compareProducts.map(p => p.servings)} />

                {/* Specifications */}
                <div className="border-b border-border bg-muted/50">
                  <div className="p-3">
                    <h3 className="font-bold text-foreground text-sm uppercase tracking-wide">
                      Specifications
                    </h3>
                  </div>
                </div>

                <ComparisonRow
                  label="Available Sizes"
                  values={compareProducts.map(p => p.sizes?.join(', '))}
                />
                <ComparisonRow
                  label="Available Flavors"
                  values={compareProducts.map(p => p.flavors?.join(', '))}
                />

                {/* Benefits */}
                <div className="border-b border-border bg-muted/50">
                  <div className="p-3">
                    <h3 className="font-bold text-foreground text-sm uppercase tracking-wide">
                      Benefits & Features
                    </h3>
                  </div>
                </div>

                <ComparisonRow
                  label="Key Benefits"
                  values={compareProducts.map(p => (
                    <ul className="space-y-1">
                      {p.benefits?.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  ))}
                />
                <ComparisonRow label="Best For" values={compareProducts.map(p => p.bestFor)} />
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10 border border-primary/20 text-center">
            <h3 className="text-2xl font-display font-bold text-foreground mb-4">
              Need Help Choosing?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Our fitness experts are here to help you select the perfect supplement for your goals.
            </p>
            <Button variant="default" size="lg" asChild>
              <a href="https://wa.me/9779824042067?text=Hi!%20I%20need%20help%20choosing%20the%20right%20supplement" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat with Expert
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// Comparison Row Component
const ComparisonRow = ({ label, values }) => {
  return (
    <div className="grid gap-4 border-b border-border last:border-b-0" style={{ gridTemplateColumns: `200px repeat(${values.length}, 1fr)` }}>
      <div className="p-4 bg-muted/30 flex items-center">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      {values.map((value, idx) => (
        <div key={idx} className="p-4 flex items-center">
          {typeof value === 'string' || typeof value === 'number' ? (
            <span className="text-sm text-foreground">{value}</span>
          ) : (
            value
          )}
        </div>
      ))}
    </div>
  );
};

export default CompareProductsPage;
